import { useEffect, useRef } from 'react';
import EditorJS from '@editorjs/editorjs';
import Embed from '@editorjs/embed';
import Table from '@editorjs/table';
import Code from '@editorjs/code';
import Header from '@editorjs/header';
import Marker from '@editorjs/marker';
import CheckList from '@editorjs/checklist';
import Delimiter from '@editorjs/delimiter';
import InlineCode from '@editorjs/inline-code';
import SimpleImage from '@editorjs/simple-image';
import NestedList from '@editorjs/nested-list';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import ImagePreviewListener from '@shared/components/others/ImagePreviewListener/ImagePreviewListener';
import './Editor.css';
import { AppLanguage } from '@shared/types';

export const EDITOR_JS_TOOLS = {
    embed: Embed,
    table: Table,
    marker: Marker,
    list: {
        class: NestedList,
        shortcut: 'CMD+SHIFT+L',
        inlineToolbar: true,
    },
    code: {
        class: Code,
        shortcut: 'CMD+SHIFT+C',
        inlineToolbar: true,
    },
    header: {
        class: Header,
        inlineToolbar: true,
        config: {
            placeholder: 'Enter a header',
            levels: [1, 2, 3, 4, 5, 6],
            defaultLevel: 3,
        },
    },
    checklist: {
        class: CheckList,
        inlineToolbar: true,
    },
    delimiter: Delimiter,
    inlineCode: InlineCode,
    image: {
        class: SimpleImage,
        inlineToolbar: true,
        config: {},
    },
};

// zIndex -  if 2 or more EditorJs components need to be placed on the same page, the element that is higher in the markup should have this value higher
const Editor = ({ editorId, editorData, setEditorData, zIndex, autoFocus, placeholder, readOnly, propToRerender }) => {
    const ejInstance = useRef(null);
    const ejContainerRef = useRef(null);
    const { i18n } = useTranslation();

    useEffect(() => {
        if (!ejInstance.current) {
            if (editorData !== undefined) {
                initEditor();
            }
        }

        return () => {
            if (ejInstance.current) {
                ejInstance.current.destroy();
                ejInstance.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propToRerender]);

    function initEditor() {
        const editor = new EditorJS({
            holder: ejContainerRef.current,
            logLevel: 'ERROR',
            data: editorData,
            onReady: () => {
                ejInstance.current = editor;
            },
            onChange: (api) => {
                (async () => {
                    const content = await api.saver.save();
                    setEditorData(content);
                })();
            },
            placeholder: placeholder,
            autofocus: autoFocus,
            readOnly: readOnly,
            tools: EDITOR_JS_TOOLS,
            i18n:
                i18n.language === AppLanguage.RU
                    ? {
                          messages: {
                              ui: {
                                  blockTunes: {
                                      toggler: {
                                          'Click to tune': 'Нажмите, чтобы настроить',
                                          'or drag to move': 'или перетащите',
                                      },
                                  },
                                  inlineToolbar: {
                                      converter: {
                                          'Convert to': 'Конвертировать в',
                                      },
                                  },
                                  toolbar: {
                                      toolbox: {
                                          Add: 'Добавить',
                                      },
                                  },
                              },
                              toolNames: {
                                  Text: 'Параграф',
                                  Heading: 'Заголовок',
                                  List: 'Список',
                                  Warning: 'Примечание',
                                  Checklist: 'Чеклист',
                                  Quote: 'Цитата',
                                  Code: 'Код',
                                  Delimiter: 'Разделитель',
                                  'Raw HTML': 'HTML-фрагмент',
                                  Table: 'Таблица',
                                  Link: 'Ссылка',
                                  Marker: 'Маркер',
                                  Bold: 'Полужирный',
                                  Italic: 'Курсив',
                                  InlineCode: 'Моноширинный',
                              },
                              tools: {
                                  warning: {
                                      Title: 'Название',
                                      Message: 'Сообщение',
                                  },
                                  link: {
                                      'Add a link': 'Вставьте ссылку',
                                  },
                                  stub: {
                                      'The block can not be displayed correctly.': 'Блок не может быть отображен',
                                  },
                                  table: {
                                      'Add row above': 'Добавить строку выше',
                                      'Add row below': 'Добавьте строку ниже',
                                      'Delete row': 'Удалить строку',
                                      'Add column to left': 'Добавить столбец слева',
                                      'Add column to right': 'Добавить столбец справа',
                                      'Delete column': 'Удалить столбец',
                                      'With headings': 'С заголовками',
                                      'Without headings': 'Без заголовков',
                                  },
                                  list: {
                                      Unordered: 'Неупорядоченный',
                                      Ordered: 'Упорядоченный',
                                  },
                                  header: {
                                      'Heading 1': 'Заголовок 1',
                                      'Heading 2': 'Заголовок 2',
                                      'Heading 3': 'Заголовок 3',
                                      'Heading 4': 'Заголовок 4',
                                      'Heading 5': 'Заголовок 5',
                                      'Heading 6': 'Заголовок 6',
                                      'Enter a header': 'Введите заголовок',
                                  },
                                  code: {
                                      'Enter a code': 'Введите код',
                                  },
                              },
                              blockTunes: {
                                  delete: {
                                      Delete: 'Удалить',
                                      'Click to delete': 'Нажмите для удаления',
                                  },
                                  moveUp: {
                                      'Move up': 'Переместить вверх',
                                  },
                                  moveDown: {
                                      'Move down': 'Переместить вниз',
                                  },
                              },
                          },
                      }
                    : {
                          messages: {},
                      },
        });
    }

    return (
        <ImagePreviewListener>
            <Box
                class={readOnly && 'readonly'}
                ref={ejContainerRef}
                spellCheck="false"
                sx={{ position: 'relative', zIndex: zIndex }}
            />
        </ImagePreviewListener>
    );
};

export default Editor;
